import type { CaptureResult } from "./posthog.js";

/**
 * Exceptions that analytics should not send.
 *
 * PropelAuth refreshes the session in the background, from a timer and from
 * focus and online listeners, and nothing catches that promise. When the
 * browser gets no response at all, the fetch rejects with a `TypeError` and
 * error tracking records an unhandled rejection. Nobody sees it fail: the SDK
 * retries, and `Gate` shows its own screen if the provider stays unreachable.
 *
 * The match is on the file, not the function names, because this runs in the
 * browser before symbolication and minification has renamed nearly every
 * function in the stack. Vite puts the SDK in its own `propelauth-*.js` chunk
 * for this reason. Every frame must come from that chunk, so a network failure
 * that passes through our own code is still captured.
 */
const NETWORK_FAILURE = new Set([
  "Failed to fetch", // Chrome
  "NetworkError when attempting to fetch resource.", // Firefox
  "Load failed", // Safari
]);

const PROPELAUTH_CHUNK = /\/static\/propelauth-[^/]+\.js$/;

interface ExceptionEntry {
  type?: string;
  value?: string;
  stacktrace?: { frames?: { filename?: string }[] };
}

function isPropelAuthNetworkFailure(exception: ExceptionEntry): boolean {
  const frames = exception.stacktrace?.frames ?? [];
  return (
    exception.type === "TypeError" &&
    NETWORK_FAILURE.has(exception.value ?? "") &&
    frames.length > 0 &&
    frames.every((frame) => PROPELAUTH_CHUNK.test(frame.filename ?? ""))
  );
}

export function dropPropelAuthRefreshFailures(event: CaptureResult | null): CaptureResult | null {
  if (event?.event !== "$exception") return event;
  const exceptions = event.properties.$exception_list as ExceptionEntry[] | undefined;
  if (exceptions?.length && exceptions.every(isPropelAuthNetworkFailure)) return null;
  return event;
}
